import React, { Component } from 'react';
import SettingsAPI from './SettingsAPI';
import PropertiesAPI from '../Properties/PropertiesAPI';
import GroupSettings from './GroupSettings';
import { FeatureFlagContext } from '../../Context/FeatureFlagContext';
import { PageContainer, Toaster, Alert } from '@knockrentals/knock-react';

export const PAGE_LOAD_ERROR_MESSAGE =
  'An unexpected error has occurred. Please try again later.';

export const TODO_PREFERENCES_LOAD_ERROR_MESSAGE =
  'Unable to load to-do list thresholds. Please try again later.';

class SettingsPage extends Component {
  state = {
    isLoading: true,
    groups: [],
    properties: [],
    selectedGroupId: 'all',
    todoPreferencesByLeasingTeamId: {},
  };

  async componentDidMount() {
    await Promise.all([this.loadCoreSettings(), this.loadTodoPreferences()]);
    this.setState({ isLoading: false });
  }

  async loadCoreSettings() {
    try {
      const [settingsResponse, propertiesResponse] = await Promise.all([
        SettingsAPI.getSettings(),
        PropertiesAPI.getCommunityInfo(),
      ]);

      const groups = settingsResponse.leasing_team_settings.sort(
        (groupA, groupB) => {
          return groupA.group_name > groupB.group_name ? 1 : -1;
        },
      );

      this.setState({
        groups: groups,
        properties: propertiesResponse.properties,
      });
    } catch {
      Toaster.showToast(
        PAGE_LOAD_ERROR_MESSAGE,
        2000,
        Toaster.ToastClasses.error,
      );
    }
  }

  async loadTodoPreferences() {
    if (!this.context.isLeasingTeamTodoThresholdsEnabled) {
      return;
    }

    try {
      const todoPreferencesByLeasingTeamId =
        await SettingsAPI.getAllLeasingTeamTodoPreferences();
      this.setState({ todoPreferencesByLeasingTeamId });
    } catch {
      Toaster.showToast(
        TODO_PREFERENCES_LOAD_ERROR_MESSAGE,
        2000,
        Toaster.ToastClasses.error,
      );
    }
  }

  render() {
    return (
      <PageContainer isLoading={this.state.isLoading}>
        <h1>Settings</h1>

        <div className="knock-react-flex knock-react-flex-row">
          <div>
            <div>Leasing team</div>
            <select
              value={this.state.selectedGroupId}
              onChange={this.onGroupChanged.bind(this)}
            >
              <option value={'all'}>All</option>
              {this.renderGroupOptions()}
            </select>
          </div>
        </div>

        {this.renderGroups()}
      </PageContainer>
    );
  }

  onGroupChanged(event) {
    this.setState({ selectedGroupId: event.target.value });
  }

  renderGroupOptions() {
    return this.state.groups.map((group) => {
      return (
        <option key={group.group_id} value={group.group_id}>
          {group.group_name}
        </option>
      );
    });
  }

  getPropertiesUnderGroup = (groupId) => {
    return this.state.properties.filter(
      (property) => property.leasing_team_id === groupId,
    );
  };

  renderGroups() {
    if (this.state.groups.length === 0) {
      return <Alert>No properties to display.</Alert>;
    }

    if (this.state.selectedGroupId === 'all') {
      return this.state.groups.map((group) => {
        return (
          <GroupSettings
            group={group}
            properties={this.getPropertiesUnderGroup(group.group_id)}
            todoPreferences={
              this.state.todoPreferencesByLeasingTeamId[group.group_id] || []
            }
            key={group.group_id}
            onSettingsChanged={this.onSettingsChanged.bind(this)}
            saveTodoPreferences={this.saveTodoPreferences}
          />
        );
      });
    }

    const selectedGroupId = parseInt(this.state.selectedGroupId, 10);

    return (
      <GroupSettings
        group={this.state.groups.find(
          (group) => group.group_id === selectedGroupId,
        )}
        key={this.state.selectedGroupId}
        properties={this.getPropertiesUnderGroup(selectedGroupId)}
        todoPreferences={
          this.state.todoPreferencesByLeasingTeamId[selectedGroupId] || []
        }
        onSettingsChanged={this.onSettingsChanged.bind(this)}
        saveTodoPreferences={this.saveTodoPreferences}
      />
    );
  }

  onSettingsChanged(groupId, updatedSettings) {
    const updatedGroups = this.state.groups.slice();

    updatedGroups[
      updatedGroups.findIndex((group) => group.group_id === groupId)
    ] = updatedSettings;

    this.setState({ groups: updatedGroups }, () => {
      SettingsAPI.updateGroupSettings(groupId, updatedSettings)
        .then(() => {
          Toaster.showToast('Saved!', 2000, Toaster.ToastClasses.success);
        })
        .catch(() => {
          Toaster.showToast(
            'Error saving leasing team settings.',
            2000,
            Toaster.ToastClasses.error,
          );
        });
    });
  }

  updateTodoPreferences = (leasingTeamId, preferenceUpdate) => {
    this.setState((prevState) => {
      const currentTodoPreferences =
        prevState.todoPreferencesByLeasingTeamId[leasingTeamId] || [];

      const updatedTodoPreferences = currentTodoPreferences.map((preference) =>
        preference.todoRuleId === preferenceUpdate.todoRuleId
          ? { ...preferenceUpdate }
          : preference,
      );

      return {
        todoPreferencesByLeasingTeamId: {
          ...prevState.todoPreferencesByLeasingTeamId,
          [leasingTeamId]: updatedTodoPreferences,
        },
      };
    });
  };

  saveTodoPreferences = ({
    leasingTeamId,
    previousPreference,
    updatedPreference,
  }) => {
    this.updateTodoPreferences(leasingTeamId, updatedPreference);

    SettingsAPI.updateLeasingTeamTodoPreferences(
      leasingTeamId,
      updatedPreference,
    )
      .then(() => {
        Toaster.showToast('Saved!', 2000, Toaster.ToastClasses.success);
      })
      .catch(() => {
        this.updateTodoPreferences(leasingTeamId, previousPreference);
        Toaster.showToast(
          'Error saving to-do list thresholds.',
          2000,
          Toaster.ToastClasses.error,
        );
      });
  };
}

SettingsPage.contextType = FeatureFlagContext;

export default SettingsPage;
